import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart3, FileText, MessageSquare, HardDrive, DollarSign, TrendingUp, Folder, Mic, Brain, Clock } from 'lucide-react';

const ProjectAnalytics = ({ projectId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    if (projectId) loadAnalytics();
  }, [projectId, period]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('oracle_token');
      const res = await axios.get(`/api/projects/${projectId}/analytics?days=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) setData(res.data);
    } catch (e) {
      console.error('[Analytics] Erreur:', e);
    } finally {
      setLoading(false);
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatCost = (usd) => usd < 0.01 ? '<0.01$' : `${usd.toFixed(2)}$`;

  if (loading && !data) {
    return (
      <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-secondary)' }}>
        <div style={{ width: 32, height: 32, border: '2px solid var(--card-border)', borderTop: '2px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
        Chargement analytics...
      </div>
    );
  }

  if (!data) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Aucune donnée</div>;

  const { costs, files, sessions, folders, studio, budget, memory } = data;

  // Chart helpers
  const maxCostDaily = Math.max(...costs.daily.map(d => d.cost), 0.001);
  const maxMsgDaily = Math.max(...sessions.messages_daily.map(d => d.count), 1);

  const SERVICE_COLORS = { llm: '#2563eb', tts: '#8b5cf6', whisper: '#059669', embedding: '#f59e0b', supadata: '#ef4444' };
  const SERVICE_LABELS = { llm: 'LLM', tts: 'TTS', whisper: 'Whisper', embedding: 'Embedding', supadata: 'Supadata' };

  // Donut data
  const totalServiceCost = Math.max(costs.by_service.reduce((s, r) => s + r.cost, 0), 0.001);
  let donutGradient = 'var(--card-border)';
  if (costs.by_service.length > 0) {
    let acc = 0;
    const segments = costs.by_service.map(s => {
      const start = acc;
      acc += (s.cost / totalServiceCost) * 100;
      return `${SERVICE_COLORS[s.service_type] || '#6b7280'} ${start}% ${acc}%`;
    });
    donutGradient = `conic-gradient(${segments.join(', ')})`;
  }

  const budgetPct = budget.percent || 0;
  const budgetColor = budgetPct >= 100 ? '#ef4444' : budgetPct >= 80 ? '#f59e0b' : '#22c55e';

  const cardStyle = { background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, padding: 16 };
  const sectionTitle = (icon, label) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
      {icon} {label}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 1100 }}>

      {/* Period selector */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
        {[7, 14, 30].map(d => (
          <button key={d} onClick={() => setPeriod(d)}
            style={{
              padding: '6px 14px', fontSize: 12, fontWeight: period === d ? 600 : 400,
              background: period === d ? 'rgba(59,130,246,0.15)' : 'var(--input-bg)',
              color: period === d ? '#3b82f6' : 'var(--text-secondary)',
              border: `1px solid ${period === d ? 'rgba(59,130,246,0.3)' : 'var(--card-border)'}`,
              borderRadius: 6, cursor: 'pointer', transition: 'all 0.15s'
            }}>
            {d}j
          </button>
        ))}
      </div>

      {/* Section 1: Top stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        {[
          { icon: <DollarSign size={20} style={{ color: '#f59e0b' }} />, label: 'Coût total', value: formatCost(costs.total_usd) },
          { icon: <FileText size={20} style={{ color: '#3b82f6' }} />, label: 'Fichiers', value: `${files.total}`, sub: formatSize(files.total_size) },
          { icon: <MessageSquare size={20} style={{ color: '#22c55e' }} />, label: 'Messages', value: `${sessions.total_messages}`, sub: `${sessions.active} sessions` },
          { icon: <HardDrive size={20} style={{ color: '#059669' }} />, label: 'ITRIIA Cloud', value: `${files.synced_count}` },
          { icon: <TrendingUp size={20} style={{ color: budgetColor }} />, label: 'Budget', value: budget.configured !== false ? `${budgetPct}%` : 'N/C', sub: budget.configured !== false ? `${budget.spent_usd?.toFixed(2) || 0}$ / ${budget.budget_usd || 0}$` : '' },
        ].map((s, i) => (
          <div key={i} style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {s.icon}
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{s.label}</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>{s.value}</div>
                {s.sub && <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{s.sub}</div>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Section 2: Budget progress */}
      {budget.configured !== false && budget.budget_usd > 0 && (
        <div style={cardStyle}>
          {sectionTitle(<DollarSign size={16} style={{ color: '#f59e0b' }} />, 'Budget')}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
            <span style={{ color: 'var(--text-secondary)' }}>{budget.spent_usd?.toFixed(2) || 0}$ dépensé</span>
            <span style={{ color: budgetColor, fontWeight: 600 }}>{budgetPct}%</span>
          </div>
          <div style={{ height: 8, background: 'var(--input-bg)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.min(budgetPct, 100)}%`, background: budgetColor, borderRadius: 4, transition: 'width 0.3s' }} />
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
            Limite : {budget.budget_usd}$ • Période : {budget.period_type || 'monthly'}
          </div>
        </div>
      )}

      {/* Section 3: Cost timeline + Section 4: Ventilation */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
        {/* Cost timeline */}
        <div style={cardStyle}>
          {sectionTitle(<BarChart3 size={16} style={{ color: '#2563eb' }} />, `Coûts (${period} jours)`)}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 80 }}>
            {costs.daily.map((d, i) => (
              <div key={i} title={`${d.date}: ${formatCost(d.cost)}`}
                style={{
                  flex: 1, borderRadius: '2px 2px 0 0', minWidth: 2,
                  height: `${Math.max((d.cost / maxCostDaily) * 100, 2)}%`,
                  background: d.cost > 0 ? '#2563eb' : 'var(--card-border)',
                  opacity: d.cost > 0 ? 1 : 0.3,
                  transition: 'height 0.2s'
                }} />
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'var(--text-muted)', marginTop: 4 }}>
            <span>{costs.daily[0]?.date?.slice(5)}</span>
            <span>{costs.daily[costs.daily.length - 1]?.date?.slice(5)}</span>
          </div>
        </div>

        {/* Cost by service donut + by model bars */}
        <div style={cardStyle}>
          {sectionTitle(<DollarSign size={16} style={{ color: '#8b5cf6' }} />, 'Ventilation coûts')}
          <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
            {/* Donut */}
            <div style={{ position: 'relative', width: 90, height: 90, flexShrink: 0 }}>
              <div style={{ width: 90, height: 90, borderRadius: '50%', background: donutGradient }} />
              <div style={{ position: 'absolute', inset: 20, borderRadius: '50%', background: 'var(--card-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)' }}>{formatCost(costs.total_usd)}</span>
              </div>
            </div>
            {/* Legend */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {costs.by_service.map(s => (
                <div key={s.service_type} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: SERVICE_COLORS[s.service_type] || '#6b7280', flexShrink: 0 }} />
                  <span style={{ color: 'var(--text-secondary)', flex: 1 }}>{SERVICE_LABELS[s.service_type] || s.service_type}</span>
                  <span style={{ color: 'var(--text)', fontWeight: 500 }}>{formatCost(s.cost)}</span>
                </div>
              ))}
              {costs.by_service.length === 0 && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Aucune donnée</div>}
            </div>
          </div>
          {/* Top models */}
          {costs.by_model.length > 0 && (
            <div style={{ marginTop: 14, borderTop: '1px solid var(--card-border)', paddingTop: 10 }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Top modèles</div>
              {costs.by_model.map(m => {
                const maxModelCost = Math.max(...costs.by_model.map(x => x.cost), 0.001);
                return (
                  <div key={m.model} style={{ marginBottom: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, marginBottom: 2 }}>
                      <span style={{ color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}>{m.model.split('/').pop()}</span>
                      <span style={{ color: 'var(--text)', fontWeight: 500 }}>{formatCost(m.cost)}</span>
                    </div>
                    <div style={{ height: 4, background: 'var(--input-bg)', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${(m.cost / maxModelCost) * 100}%`, background: '#2563eb', borderRadius: 2 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Section 5: Files & Folders + Section 6: Sessions */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
        {/* Files & Folders */}
        <div style={cardStyle}>
          {sectionTitle(<FileText size={16} style={{ color: '#3b82f6' }} />, 'Fichiers & Dossiers')}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {/* File types */}
            <div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase' }}>Par type</div>
              {Object.entries(files.by_type || {}).map(([type, count]) => {
                const maxType = Math.max(...Object.values(files.by_type || {}), 1);
                const TYPE_COLORS = { Doc: '#2563eb', Txt: '#8b5cf6', Data: '#f59e0b', Img: '#22c55e', Autre: '#6b7280' };
                return (
                  <div key={type} style={{ marginBottom: 3 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, marginBottom: 1 }}>
                      <span style={{ color: 'var(--text-secondary)' }}>{type}</span>
                      <span style={{ color: 'var(--text)' }}>{count}</span>
                    </div>
                    <div style={{ height: 3, background: 'var(--input-bg)', borderRadius: 2 }}>
                      <div style={{ height: '100%', width: `${(count / maxType) * 100}%`, background: TYPE_COLORS[type] || '#6b7280', borderRadius: 2 }} />
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Phase B2 — Bases d'Expertise V2 (remplace l'ancien "Score expertise" Pierre) */}
            <div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase' }}>Bases d'expertise</div>
              {[
                { label: 'Bases attachées', count: data.expertise?.bases_count || 0, color: '#8b5cf6' },
                { label: 'Documents indexés', count: data.expertise?.docs_count || 0, color: '#a78bfa' },
              ].map(s => (
                <div key={s.label} style={{ marginBottom: 3 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, marginBottom: 1 }}>
                    <span style={{ color: 'var(--text-secondary)' }}>{s.label}</span>
                    <span style={{ color: s.color, fontWeight: 600 }}>{s.count}</span>
                  </div>
                </div>
              ))}
              {(data.expertise?.ingestion_cost_usd || 0) > 0 && (
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
                  Coût ingestion : <span style={{ color: '#8b5cf6', fontWeight: 600 }}>${data.expertise.ingestion_cost_usd.toFixed(4)}</span>
                </div>
              )}
            </div>
          </div>
          {/* Folders + cloud */}
          <div style={{ marginTop: 12, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {[
              { label: 'Dossiers', value: folders.total_folders },
              { label: 'Prof. max', value: folders.max_depth },
              { label: 'En dossiers', value: folders.files_in_folders },
              { label: 'Racine', value: folders.files_at_root },
              { label: 'Cloud', value: files.synced_count },
              { label: 'Local', value: files.local_count },
            ].map((s, i) => (
              <div key={i} style={{ padding: '4px 10px', background: 'var(--input-bg)', borderRadius: 6, fontSize: 10 }}>
                <span style={{ color: 'var(--text-muted)' }}>{s.label} </span>
                <span style={{ color: 'var(--text)', fontWeight: 600 }}>{s.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Sessions & Activity */}
        <div style={cardStyle}>
          {sectionTitle(<MessageSquare size={16} style={{ color: '#22c55e' }} />, 'Sessions & Activité')}
          {/* Message timeline */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 60, marginBottom: 6 }}>
            {sessions.messages_daily.map((d, i) => (
              <div key={i} title={`${d.date}: ${d.count} msg`}
                style={{
                  flex: 1, borderRadius: '2px 2px 0 0', minWidth: 2,
                  height: `${Math.max((d.count / maxMsgDaily) * 100, 2)}%`,
                  background: d.count > 0 ? '#22c55e' : 'var(--card-border)',
                  opacity: d.count > 0 ? 1 : 0.3
                }} />
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'var(--text-muted)', marginBottom: 12 }}>
            <span>{sessions.messages_daily[0]?.date?.slice(5)}</span>
            <span>{sessions.messages_daily[sessions.messages_daily.length - 1]?.date?.slice(5)}</span>
          </div>
          {/* Session badges */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            {[
              { label: 'Actives', value: sessions.active, color: '#22c55e' },
              { label: 'Archivées', value: sessions.archived, color: '#6b7280' },
              { label: 'Total', value: sessions.total, color: '#3b82f6' },
            ].map((s, i) => (
              <div key={i} style={{
                padding: '4px 10px', borderRadius: 6, fontSize: 11,
                background: `${s.color}15`, border: `1px solid ${s.color}30`
              }}>
                <span style={{ color: s.color, fontWeight: 600 }}>{s.value}</span>
                <span style={{ color: 'var(--text-secondary)', marginLeft: 4 }}>{s.label}</span>
              </div>
            ))}
          </div>
          {/* Studio + Memory */}
          <div style={{ borderTop: '1px solid var(--card-border)', paddingTop: 10 }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase' }}>Studio & Mémoire</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[
                { label: 'TTS', value: studio.tts_count, icon: <Mic size={10} /> },
                { label: 'Whisper', value: studio.whisper_count, icon: <Mic size={10} /> },
                { label: 'Thèmes', value: memory.themes_count, icon: <Brain size={10} /> },
                { label: 'Décisions', value: memory.decisions_count, icon: <Brain size={10} /> },
                { label: 'Conversations', value: memory.total_conversations, icon: <Clock size={10} /> },
              ].map((s, i) => (
                <div key={i} style={{ padding: '3px 8px', background: 'var(--input-bg)', borderRadius: 6, fontSize: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ color: 'var(--text-muted)' }}>{s.icon}</span>
                  <span style={{ color: 'var(--text-muted)' }}>{s.label}</span>
                  <span style={{ color: 'var(--text)', fontWeight: 600 }}>{s.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectAnalytics;
